import {currentTimeMillis, isDisabledField, isEntityEnabled} from "./ClientCache";
import {
    ajaxJSON,
    areAllEmptyOrNull,
    areEqual,
    areNotEqual,
    getFirstNotEmptyObj,
    isAnyEmptyOrNull,
    isAnyEqualToFirst,
    isEmptyOrNull
} from "../common/NamaCommon";
import {currentLoginInfo, currentUserHasEmployee} from "./LoginPresenter";
import * as ApplicableWhen from '../constants/ApplicableWhen'
import * as DomainBaseFieldIDs from '../constants/DomainBaseFieldIDs'
import * as DTOSecurityFieldAuthority from '../constants/DTOSecurityFieldAuthority';
import * as CapabilityType from '../constants/CapabilityType'
import * as EditAuthority from '../constants/EditAuthority'
import * as PrintAuthority from '../constants/PrintAuthority'
import * as GridAction from "../constants/GridAction";
import * as cookies from "js-cookie";
import * as urls from "../constants/NamaURLS";
import {NamaRouterInstance} from "../routing/NamaRouter";
import * as places from "../routing/places";
import {registerShortcutKeys} from "../common/ShortCutsUtil";
import {getQueryParams, isArabic} from "../common/Translator";
import * as DomainBaseEntities from "../constants/DomainBaseEntities";
import * as BSCEntities from "../constants/BSEntities";
import {resetNotificationsApprovalsCount} from "../widgets/ApprovalsAndNotifications";

/**
 *
 * @param targetEntity
 * @param fieldId
 * @param applicableWhen
 * @return {namaTypes.DTOSecurityFieldAuthority}
 */
function getFieldSecurityLine(targetEntity, fieldId, applicableWhen) {
    let line = calcFieldLineFromLines(targetEntity, fieldId, applicableWhen, user().settings.disabledFields);
    if (line != null)
        return line;
    if (userHasNoSecurityProfile())
        return null;
    return calcFieldLineFromLines(targetEntity, fieldId, applicableWhen, sp().disabledFields);
}

/**
 *
 * @param targetEntity
 * @param fieldId
 * @param applicableWhen
 * @param disabledFields
 * @return {namaTypes.DTOSecurityFieldAuthority}
 */
function calcFieldLineFromLines(targetEntity, fieldId, applicableWhen,
                                disabledFields) {
    let wildCardLine = null;

    for (let authority of disabledFields) {
        if (modeApplicable(authority, applicableWhen) && sameEntity(targetEntity, authority.entityType)) {
            if (fieldId === authority.fieldId)
                return authority;
            if (wildCardLine === null && "*" === authority.fieldId)
                wildCardLine = authority;
        }
    }
    for (let authority of disabledFields) {
        if (modeApplicable(authority, applicableWhen) && entityTypeListContainsEntity(authority.targetEntities, targetEntity)) {
            if (fieldId === authority.fieldId)
                return authority;
            if (wildCardLine === null && "*" === authority.fieldId)
                wildCardLine = authority;
        }
    }
    if (wildCardLine === null) {
        for (let authority of disabledFields) {
            if (modeApplicable(authority, applicableWhen)
                && areAllEmptyOrNull(authority.targetEntities, authority.entityType)) {
                if (fieldId === authority.fieldId)
                    return authority;
                if (wildCardLine === null && "*" === authority.fieldId)
                    wildCardLine = authority;
            }
        }
    }
    return wildCardLine;
}

/**
 *
 * @param {namaTypes.DTOSecurityFieldAuthority} authority
 * @param applicableWhen
 * @return {*}
 */
function modeApplicable(authority, applicableWhen) {
    switch (applicableWhen) {
        case 'NewMode':
            return authority.applicableWithNewMode === true;
        case 'UpdateMode':
            return authority.applicableWithUpdateMode === true;
        case 'DraftMode':
            return authority.applicableWithDraftMode === true;
    }
    return authority.applicableWithNewMode === true && authority.applicableWithUpdateMode === true && authority.applicableWithDraftMode === true;
}

function sameEntity(targetEntity, authorityEntityType) {
    return targetEntity === authorityEntityType;
}

/**
 *
 * @param {namaTypes.EntityReferenceData} authorityEntityTypeList
 * @param targetEntity
 * @return {*}
 */
function entityTypeListContainsEntity(authorityEntityTypeList, targetEntity) {
    if (!authorityEntityTypeList)
        return false;
    /**
     *
     * @type {string}
     */
    let entityTypes = authorityEntityTypeList.extraData[DomainBaseFieldIDs.EntityTypeList_lines_entityType] || "";
    return entityTypes.indexOf("," + targetEntity + ",") >= 0;
}

export function canViewAllMenuItems() {
    let logInInfo = currentLoginInfo();
    if (!logInInfo)
        return false;
    let canViewAllMenuItems = logInInfo.userInfo.settings.canViewMenuItems;
    if (canViewAllMenuItems)
        return true;
    if (!logInInfo.userInfo.alternativeUsers)
        return false;
    for (let user of logInInfo.userInfo.alternativeUsers) {
        if (user.settings && user.settings.canViewMenuItems)
            return true;
    }
    return false;
}

export function userCanViewField(entityType, fieldId) {
    if (userHasNoSecurityProfile())
        return false;
    if (isFullAuthority())
        return true;
    if (isAnyEmptyOrNull(entityType, fieldId))
        return true;
    if (!isEntityEnabled(entityType))
        return false;

    let authority = getFieldSecurityLine(entityType, fieldId, ApplicableWhen.All);
    if (!authority)
        return true;
    return authority.authorityType !== DTOSecurityFieldAuthority.InVisible;
}

export function userCanEditField(entityType, fieldId, applicableWhen) {
    if (isDisabledField(entityType, fieldId))
        return false;
    if (entityType == null)
        return true;
    if (!currentLoginInfo() || !currentLoginInfo().loggedIn)
        return false;
    return isEntityEnabled(entityType) && userCanEditConsiderAlternative(entityType, fieldId, applicableWhen);
}

function userCanEditConsiderAlternative(entityType, fieldId, applicableWhen) {
    let userInfo = currentLoginInfo().userInfo;
    let canEditField = userCanEditField_(entityType, fieldId, applicableWhen, userInfo);
    if (isSpecified(canEditField))
        return canEditField;

    for (let user of userInfo.alternativeUsers) {
        let canEdit = userCanEditField_(entityType, fieldId, applicableWhen, user);
        if (isSpecified(canEdit))
            return canEdit;
    }
    return true;
}

function isNotSpecified(specified) {
    return specified === "NotSpecified";
}

function isSpecified(specified) {
    return !isNotSpecified(specified);
}

function userCanEditField_(targetEntity, fieldId, applicableWhen, user) {
    if (!user.fullProfile)
        return false;
    if (areAllEmptyOrNull(user.settings.disabledFields, user.fullProfile.disabledFields))
        return "NotSpecified";
    if (!targetEntity || !fieldId)
        return true;
    if (user.fullProfile.fullAuthority === true)
        return true;
    let authority = getFieldSecurityLine(targetEntity, fieldId, applicableWhen);
    if (!authority)
        return "NotSpecified";
    return areNotEqual(authority.authorityType, DTOSecurityFieldAuthority.Disabled) ? true : false;
}

export function userCan(entityType, capability) {
    if (!entityType)
        return false;
    if (!currentLoginInfo() || !currentLoginInfo().loggedIn)
        return false;
    return isEntityEnabled(entityType) && userCanConsiderAlternative(entityType, capability);
}

function userCanConsiderAlternative(entityType, capability) {
    if (isMandatoryCapability(entityType, capability))
        return true;
    let currentUserCan = userCan_(entityType, capability, currentLoginInfo().userInfo);
    if (currentUserCan)
        return currentUserCan;
    let alternativeUsers = currentLoginInfo().userInfo.alternativeUsers;
    for (let user of alternativeUsers) {
        if (userCan_(entityType, capability, user))
            return true;
    }
    return false;
}

function isMandatoryCapability(entityType, capability) {
    return isAnyEqualToFirst(entityType, DomainBaseEntities.DocumentBook, DomainBaseEntities.DocumentTerm,
        DomainBaseEntities.MasterGroup, BSCEntities.Currency)
        && isAnyEqualToFirst(capability, CapabilityType.ListView, CapabilityType.RecordView, CapabilityType.SearchView);
}

function userCan_(entityType, capabilityId, user) {
    if (!user.fullProfile || !user.fullProfile.standardLines)
        return false;
    if (user.fullProfile.fullAuthority)
        return true;
    if (!entityType || !capabilityId)
        return true;
    let userCan = internalUserCan(entityType, capabilityId, user.settings.standardLines);
    if (userCan !== null)
        return userCan;
    userCan = internalUserCan(entityType, capabilityId, user.fullProfile.standardLines);
    if (userCan !== null)
        return userCan;
    return false;
}

export function userCanApprove(entityType) {
    return currentUserHasEmployee() && userCan(entityType, CapabilityType.Approve);
}
/**
 *
 * @param entityType
 * @param capabilityId
 * @param {namaTypes.DTOStandardSecurityLine[]} standardLines
 * @return {*}
 */
function internalUserCan(entityType, capabilityId, standardLines) {
    for (let userCapability of standardLines) {
        if (userCapability.targetEntity === entityType)
            return userCanCapability(userCapability, capabilityId);
    }
    for (let userCapability of standardLines) {
        if (entityTypeListContainsEntity(userCapability.targetEntities, entityType))
            return userCanCapability(userCapability, capabilityId);
    }
    for (let userCapability of standardLines) {
        if (areAllEmptyOrNull(userCapability.targetEntities, userCapability.targetEntity))
            return userCanCapability(userCapability, capabilityId);
    }
    return null;
}

/**
 *
 * @param {namaTypes.DTOStandardSecurityLine} userCapability
 * @param capabilityId
 */
function userCanCapability(userCapability, capabilityId) {
    if (capabilityId === CapabilityType.Commit)
        return canCommit(userCapability);
    if (capabilityId === CapabilityType.CreateDraft)
        return userCapability.canEdit === EditAuthority.SaveDraft || canCommit(userCapability);
    if (capabilityId === CapabilityType.SaveDraft)
        return (userCapability.canEdit === EditAuthority.SaveDraft || canCommit(userCapability)) && !userCapability.preventSaveDraft;
    if (capabilityId === CapabilityType.Delete)
        return userCapability.canDelete;
    if (capabilityId === CapabilityType.DeleteDraft) {
        if ("Yes" === userCapability.draftDeletionCapability)
            return true;
        else if ("No" === userCapability.draftDeletionCapability)
            return false;
        return userCapability.canDelete;
    }
    if (capabilityId === CapabilityType.ChangeCapability)
        return userCapability.canChangeCapability || userCapability.canEdit === EditAuthority.EditAfterCommit;
    if (capabilityId === CapabilityType.EditAfterCommit)
        return userCapability.canEdit === EditAuthority.EditAfterCommit;
    if (capabilityId === CapabilityType.ListView)
        return userCapability.listView;
    if (capabilityId === CapabilityType.SearchView)
        return userCapability.searchView || userCapability.listView;
    if (capabilityId === CapabilityType.PrintMoreThanOnce)
        return userCapability.canPrint === PrintAuthority.MoreThanOne;
    if (capabilityId === CapabilityType.PrintOnce)
        return userCapability.canPrint === PrintAuthority.One || userCapability.canPrint === PrintAuthority.MoreThanOne;
    if (capabilityId === CapabilityType.RecordView)
        return userCapability.recordView;
    if (capabilityId === CapabilityType.Approve)
        return true;
    if (capabilityId === CapabilityType.Revise)
        return userCapability.canRevise;
    if (capabilityId === CapabilityType.Unrevise)
        return userCapability.canUnRevise;
    if (capabilityId === CapabilityType.Duplicate)
        return userCapability.canDuplicate;
    if (capabilityId === CapabilityType.Export)
        return userCapability.canExport;
    if (capabilityId === CapabilityType.Import)
        return userCapability.canImport;
    if (capabilityId.startsWith(CapabilityType.Revise)) {
        return canPerformOnLevel(capabilityId.replaceAll(CapabilityType.Revise, ""), userCapability.reviseLevels, userCapability.canRevise);
    } else if (capabilityId.startsWith(CapabilityType.Unrevise)) {
        return canPerformOnLevel(capabilityId.replaceAll(CapabilityType.Unrevise, ""), userCapability.unreviseLevels, userCapability.canUnRevise);
    }
    if (capabilityId === CapabilityType.CanViewTransaction)
        return !userCapability.preventViewSystemTransaction;
    if (capabilityId === CapabilityType.CanViewMoreMenu)
        return !userCapability.preventViewMoreMenu;
    return false;
}

function canPerformOnLevel(levelStr, allowedLevels, userCan) {
    if (userCan === false)
        return false;
    if (!allowedLevels)
        return true;
    let level = 0;
    if (levelStr)
        level = parseInt(levelStr.toUpperCase().replace("L", ""));
    let sep = ",";
    if (!allowedLevels.contains(sep))
        sep = "-";
    let allowedLevelsList = allowedLevels.toUpperCase().replace("L", "").split(se);
    for (let allowedLevelStr in allowedLevelsList) {
        let allowedLevel = parseInt(allowedLevelStr);
        if (allowedLevel === level)
            return true;
    }
    return false;
}

export function userCanAction(entityType, actionId) {
    if (userHasNoSecurityProfile())
        return false;
    if (isFullAuthority())
        return true;
    if (isAnyEmptyOrNull(entityType, actionId))
        return true;
    let actionSecurityLine = getActionSecurityLin(entityType, actionId);
    if (actionSecurityLine) {
        return actionSecurityLine.authorityType === "enabled";
    }
    return true;
}

function getActionSecurityLin(targetEntity, actionId) {
    if (userHasNoSecurityProfile())
        return null;
    let wildCardLine = null;
    for (let authority of sp().actionLines) {
        if (sameEntity(targetEntity, authority.targetEntity, authority.targetEntities)) {
            if (actionId === authority.actionId)
                return authority;
            if (wildCardLine == null && "*" === authority.actionId)
                wildCardLine = authority;
        }
    }
    for (let authority of sp().actionLines) {
        if (entityTypeListContainsEntity(authority.targetEntities, targetEntity)) {
            if (actionId === authority.actionId)
                return authority;
            if (wildCardLine == null && "*" === authority.actionId)
                wildCardLine = authority;
        }
    }
    if (wildCardLine == null) {
        for (let authority of sp().actionLines) {
            if (areAllEmptyOrNull(authority.targetEntities, authority.targetEntity)) {
                if (actionId === authority.actionId)
                    return authority;
                if (wildCardLine == null && "*" === authority.actionId)
                    wildCardLine = authority;
            }
        }
    }
    return wildCardLine;
}

/**
 *
 * @param {namaTypes.DTOStandardSecurityLine} userCapability
 * @return {*}
 */
function canCommit(userCapability) {
    return userCapability.canEdit === EditAuthority.Commit || userCapability.canEdit === EditAuthority.EditAfterCommit;
}

/**
 *
 * @return {namaTypes.DTOSecurityProfile}
 */
function sp() {
    return currentLoginInfo().userInfo.fullProfile;
}

/**
 *
 * @return {namaTypes.DTOUser}
 */
export function user() {
    if (currentLoginInfo())
        return currentLoginInfo().userInfo;
    return undefined;
}

export function isFullAuthority() {
    if (userHasNoSecurityProfile())
        return false;
    return sp().fullAuthority;
}

export function userHasNoSecurityProfile() {
    return isEmptyOrNull(sp());
}

function userCanNotOnALine(user, entityType, fieldId, applicableWhen, gridAction) {
    let securityProfile = user.fullProfile;
    if (!securityProfile)
        return true;
    if (!(securityProfile.disabledFields))
        return false;
    if (!entityType || !fieldId)
        return false;
    if (securityProfile.fullAuthority)
        return false;
    let authority = getFieldSecurityLine(entityType, fieldId, applicableWhen);
    if (!authority)
        return false;
    if (gridAction === GridAction.DeleteLine)
        return authority.preventDeleteLine;
    if (gridAction === GridAction.InsertLine)
        return authority.preventInsertLine;
    return authority.preventCopyLine;
}

export function userCanNotOnALineConsiderAlternative(entityType, fieldId, applicableWhen, gridAction) {
    let currentUser = user();
    let _userCanNotOnALine = userCanNotOnALine(currentUser, entityType, fieldId, applicableWhen, gridAction);
    if (!_userCanNotOnALine)
        return _userCanNotOnALine;
    let dtoUsers = currentUser.alternativeUsers;
    if (!dtoUsers)
        return true;
    for (let altUser of dtoUsers) {
        if (!userCanNotOnALine(altUser, entityType, fieldId, applicableWhen, gridAction))
            return false;
    }
    return true;
}

export function userCanNOTInsertOrDeleteLine(entityType, fieldId, applicableWhen, gridAction) {
    if (!entityType)
        return true;
    if (!currentLoginInfo() || !currentLoginInfo().loggedIn)
        return true;
    if (!isEntityEnabled(entityType))
        return true;
    return userCanNotOnALineConsiderAlternative(entityType, fieldId, applicableWhen, gridAction);
}

export function userCanNOTCopyLine(entityType, fieldId, applicableWhen) {
    if (!entityType)
        return true;
    if (!currentLoginInfo() || !currentLoginInfo().loggedIn)
        return true;
    if (!isEntityEnabled(entityType))
        return true;
    return userCanNotOnALineConsiderAlternative(entityType, fieldId, applicableWhen);
}

export function isCurrentLogInInfoValid() {
    if (currentLoginInfo() && currentLoginInfo().loggedIn)
        return true;
    return false;
}

export function userCanView(entityType) {
    return userCan(entityType, CapabilityType.RecordView);
}

export function userCanList(entityType) {
    return userCan(entityType, CapabilityType.ListView);
}

export function userCanSearch(entityType) {
    return userCan(entityType, CapabilityType.SearchView);
}

export function userCanCreate(entityType) {
    return userCan(entityType, CapabilityType.CreateDraft);
}

export function userCanEdit(entityType) {
    return userCan(entityType, CapabilityType.Commit);
}

export function userCanNotEdit(entityType) {
    return !userCanEdit(entityType);
}

export function userCanNotCreate(entityType) {
    return !userCanCreate(entityType);
}

export function userCanDelete(entityType) {
    return userCan(entityType, CapabilityType.Delete);
}

export function userCanDeleteDraft(entityType) {
    return userCan(entityType, CapabilityType.DeleteDraft);
}

export function fetchMinSearchQueryLength() {
    //TODO: gui2 missing feature
    return 0;
}


function calcPageSecurity(targetEntity, pageId, pageNumber, lines) {
    if (isEmptyOrNull(lines))
        return DTOSecurityFieldAuthority.NotDefined;
    for (let authority of lines) {
        if ((sameEntity(targetEntity, authority.entityType, authority.targetEntities) || entityTypeListContainsEntity(authority.entityTypeList, targetEntity)
            || areAllEmptyOrNull(authority.entityType, authority.entityTypeList)) && (
            areEqual(pageId, authority.pageName) ||
            areEqual("" + (pageNumber + 1), authority.pageName)))
            return authority.authorityType;
    }
    return DTOSecurityFieldAuthority.NotDefined;
}

function getPageAuthorityForUser(user, entityType, pageId, pageIndex) {
    if (user.fullProfile == null)
        return DTOSecurityFieldAuthority.Normal;
    if (entityType == null)
        return DTOSecurityFieldAuthority.Normal;
    if (user.fullProfile.fullAuthority === true)
        return DTOSecurityFieldAuthority.Normal;
    if (areAllEmptyOrNull(user.settings.pageSecurity, user.fullProfile.pageSecurity))
        return DTOSecurityFieldAuthority.NotDefined;
    let pageSecurity = calcPageSecurity(entityType, pageId, pageIndex, user.settings.pageSecurity);
    if (areNotEqual(pageSecurity, DTOSecurityFieldAuthority.NotDefined))
        return pageSecurity;
    return calcPageSecurity(entityType, pageId, pageIndex, user.fullProfile.pageSecurity);
}

function shouldBeIgnored(authority) {
    return isEmptyOrNull(authority) || areEqual(authority, DTOSecurityFieldAuthority.NotDefined);
}

function findHigherAuthority(pageAuthority, alternateAuthority) {
    if (shouldBeIgnored(alternateAuthority))
        return pageAuthority;
    if (areEqual(alternateAuthority, DTOSecurityFieldAuthority.Normal))
        return alternateAuthority;
    if (areEqual(pageAuthority, DTOSecurityFieldAuthority.Disabled))
        return pageAuthority;
    return alternateAuthority;
}

export function getPageAuthority(entityType, resourceId, pageIndex) {
    let logInInfo = currentLoginInfo();
    if (!logInInfo)
        return false;
    let user = logInInfo.userInfo;
    let pageAuthority = getPageAuthorityForUser(user, entityType, resourceId, pageIndex);
    if (areEqual(pageAuthority, DTOSecurityFieldAuthority.Normal))
        return pageAuthority;
    let currentHighPageAuthority = null;
    for (let alternateUser of user.alternativeUsers) {
        currentHighPageAuthority = findHigherAuthority(pageAuthority, getPageAuthorityForUser(alternateUser, entityType, resourceId, pageIndex))
        if (areEqual(currentHighPageAuthority, DTOSecurityFieldAuthority.Normal))
            return currentHighPageAuthority;
        pageAuthority = currentHighPageAuthority;
    }
    return getFirstNotEmptyObj(currentHighPageAuthority, pageAuthority);
}

export function changeLocale_() {
    let search = window.location.search;
    let queryParams = getQueryParams(search);
    queryParams["locale"] = isArabic() ? "en" : "ar";
    let newSearch = "";
    for (let p in queryParams) {
        if (newSearch)
            newSearch += "&";
        newSearch = p + "=" + queryParams[p];
    }
    window.location.search = "?" + newSearch;
}

function logoutCallBack() {
    let currentPlace = NamaRouterInstance.currentPresenter.place;
    let loginPlace = new places.LoginPlace();
    loginPlace.prevPlace = currentPlace;
    NamaRouterInstance.goTo(loginPlace);
    localStorage.setItem("lastlogouttimestamp", currentTimeMillis() + "");
    registerShortcutKeys(null);
    resetNotificationsApprovalsCount();
}

export function logout() {
    let logoutInfo =
        {
            deviceId: cookies.get("dmsdeviceid"),
        };
    cookies.remove("dmsdeviceid");
    ajaxJSON(urls.LOGOUT, {
            logoutInfo: JSON.stringify(logoutInfo)
        }, (result) => {
        logoutCallBack();
        }, () => {
        logoutCallBack();
        }
    );
}